import React, { useState, useMemo, useEffect } from 'react';
import { X, Plus, AlertCircle, Scale, Info } from 'lucide-react';
import { formatTimeAgo, CollapsibleSection, NumCell } from './CombativesShell';
import { useProfile } from '../../context/ProfileContext';
import MediaCaptureMenu from '../MediaCaptureMenu';
import PasteUrlInput from '../PasteUrlInput';
import AttachmentTile from '../AttachmentTile';
import { fileToPayload, urlToPayload } from '../../lib/mediaCapture';
import MultiSelectPills from './MultiSelectPills';
import ChipInput from './ChipInput';
import {
  WRESTLING_STYLES,
  WRESTLING_CLASS_FORMATS,
  WRESTLING_FOCUS_MODES,
  WRESTLING_SPARRING_TRIGGERS,
  WRESTLING_MASTERY_LEVELS,
  WRESTLING_FELT_SCALE,
  WRESTLING_PHASES,
  wrestlingTechniquesForStyle,
  WRESTLING_COACH_TOPICS,
  WRESTLING_MATCH_RESULTS,
  WRESTLING_CUT_METHODS,
  WRESTLING_HYDRATION,
  WRESTLING_WEIGHT_FLAGS,
  WRESTLING_WEIGHT_REFERENCES,
} from '../../data/combativesWrestling';

const DRAFT_KEY = 'enferno_wrestling_session_draft_v1';
const STYLE_PROMPT_DISMISSED_KEY = 'enferno_wrestling_style_prompt_dismissed_v1';


// -- Factory helpers (mirror BJJ shape so the eventual shared refactor is mechanical) --
const newTechnique = () => ({
  phase: 'neutral',
  subgroup: '',
  name: '',
  reps: '',
  mastery: '',
  note: '',
});

const newCoachNote = () => ({ topic: '', note: '' });
const newMatch = () => ({ opponent: '', result: 'win', score: '', notes: '' });
const newInjury = () => ({ area: '', severity: 1, note: '' });

const newSparring = () => ({
  liveGoCount: '',
  takedownsScored: '',
  takedownsConceded: '',
  escapesEarned: '',
  escapesConceded: '',
  reversalsScored: '',
  reversalsConceded: '',
  turnsScored: '',
  turnsConceded: '',
  pinsScored: '',
  pinsConceded: '',
  ridingTimeSeconds: '',  // folkstyle only
  pushoutsScored: '',     // freestyle/greco only
  pushoutsConceded: '',
  intensityRating: 3,
  partnersWorkedWith: '',
});

const newWeight = () => ({
  scaleWeightLb: '',
  targetClassLb: '',
  daysToWeighIn: '',
  cutMethod: '',
  recoveryHoursSlept: '',
  hydrationStatus: '',
  notes: '',
});

/**
 * WrestlingLogDialog — rich session journal for Wrestling. Routed in
 * Training.jsx when art.id === 'wrestling'. Schema and copy locked
 * per `/app/memory/WRESTLING_RESEARCH.md`.
 *
 * Style handling: one technique tree, session-level `style` field, conditional
 * UI for style-specific blocks (RT folkstyle-only, push-outs freestyle/greco-only,
 * leg attacks hidden for greco). Style prompt banner appears the first time the
 * user opens the dialog and remembers their answer on the profile.
 */
const WrestlingLogDialog = ({ art, onClose, onSave, initialData = null }) => {
  const { profile, training, setProfile } = useProfile();
  const isEditMode = !!initialData;

  // --- Style preference: stored on profile.wrestlingStyle (we don't add
  // another onboarding card per founder; instead the banner below
  // captures the first answer and writes to profile silently). ---
  const profileStyle = profile?.wrestlingStyle || null;
  const [stylePromptDismissed, setStylePromptDismissed] = useState(() => {
    try { return localStorage.getItem(STYLE_PROMPT_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  const todayLocal = useMemo(() => {
    const d = new Date();
    const tzOff = d.getTimezoneOffset() * 60000;
    return new Date(d - tzOff).toISOString().slice(0, 10);
  }, []);

  const initialFormState = () => ({
    date: initialData?.date ? new Date(initialData.date).toISOString().slice(0, 10) : todayLocal,
    duration: initialData?.duration ?? '',
    style: initialData?.style || profileStyle || 'folkstyle',
    classFormat: initialData?.classFormat || 'live',
    focusModes: initialData?.focusModes || ['Going Live'],
    intent: initialData?.intent || '',
    techniques: initialData?.techniques?.length ? initialData.techniques : [newTechnique()],
    sparring: initialData?.sparring || null,
    coachFeedback: initialData?.coachFeedback || [],
    weight: initialData?.weight || null,
    matches: initialData?.matches || [],
    injuryConcerns: initialData?.injuryConcerns || [],
    reflection: initialData?.reflection || '',
    notes: initialData?.notes || '',
    attachments: initialData?.attachments || [],
  });

  const [form, setForm] = useState(initialFormState);
  const [restoredFromDraft, setRestoredFromDraft] = useState(null);
  const [error, setError] = useState('');

  // --- Draft autosave (localStorage only, never network — same posture as BJJ) ---
  useEffect(() => {
    if (isEditMode) return;
    let parsed = null;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) parsed = JSON.parse(raw);
    } catch { /* ignore */ }
    if (parsed?.savedAt && parsed?.form) {
      // Defer to a microtask so the hot-reload linter doesn't flag
      // this as set-state-during-effect; identical effect either way.
      Promise.resolve().then(() => {
        setForm(parsed.form);
        setRestoredFromDraft(parsed.savedAt);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditMode) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ savedAt: new Date().toISOString(), form }));
      } catch { /* ignore */ }
    }, 600);
    return () => clearTimeout(t);
  }, [form, isEditMode]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

  // --- Derived: style-aware technique tree + sparring trigger ---
  const styleMeta = WRESTLING_STYLES.find((s) => s.id === form.style) || WRESTLING_STYLES[0];
  const techniqueTree = useMemo(() => wrestlingTechniquesForStyle(form.style), [form.style]);
  const subgroupOptionsForPhase = (phase) =>
    techniqueTree.filter((g) => g.phase === phase).map((g) => g.subgroup);
  const techniqueOptionsFor = (phase, subgroup) => {
    const found = techniqueTree.find((g) => g.phase === phase && g.subgroup === subgroup);
    return found ? found.techniques : [];
  };

  const showSparring = useMemo(
    () => form.focusModes.some((m) => WRESTLING_SPARRING_TRIGGERS.includes(m))
      || form.classFormat === 'live'
      || form.classFormat === 'situational',
    [form.focusModes, form.classFormat],
  );
  const showMatchBlock = form.classFormat === 'match_dual' || form.classFormat === 'tournament';

  // --- Anti-shame weight guardrails (passive computation — only used for
  //     soft surface flags; never blocks the user). ---
  const weightFlag = useMemo(() => {
    if (!form.weight) return null;
    const w = form.weight;
    const hydration = w.hydrationStatus;
    if (hydration === 'red') {
      return { tone: 'urgent', message: "You flagged red hydration. Stop cutting today — get fluids and electrolytes. We're a notebook, not a doctor; if you feel off, talk to your coach or call a physician." };
    }
    // Compare today's weight to most recent prior weight log
    const priorSessions = (training?.sessions || [])
      .filter((s) => s.artId === 'wrestling' && s.weight?.scaleWeightLb)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    if (priorSessions.length > 0 && w.scaleWeightLb) {
      const prior = priorSessions[0];
      const priorWeight = parseFloat(prior.weight.scaleWeightLb);
      const today = parseFloat(w.scaleWeightLb);
      const drop = priorWeight - today;
      const days = Math.max(1, (new Date(form.date) - new Date(prior.date)) / 86_400_000);
      const dropPerDay = drop / days;
      if (dropPerDay >= WRESTLING_WEIGHT_FLAGS.rapidDropLbsPerDay) {
        return { tone: 'caution', message: `That's a ${drop.toFixed(1)}lb drop in ~${Math.round(days)} day${days === 1 ? '' : 's'}. How are you feeling? NCAA guidance is ~1.5% of body weight per week max. Make sure your coach is in the loop.` };
      }
    }
    if (hydration === 'amber') {
      return { tone: 'caution', message: 'Amber hydration — keep an eye on it. Pale urine and clear thinking are your two best signals.' };
    }
    return null;
  }, [form.weight, training?.sessions, form.date]);

  // --- Mutation helpers ---
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTechAt = (i, patch) => setForm((f) => ({
    ...f, techniques: f.techniques.map((t, idx) => idx === i ? { ...t, ...patch } : t),
  }));
  const removeTechAt = (i) => setForm((f) => ({ ...f, techniques: f.techniques.filter((_, idx) => idx !== i) }));
  const setCoachAt = (i, patch) => setForm((f) => ({
    ...f, coachFeedback: f.coachFeedback.map((c, idx) => idx === i ? { ...c, ...patch } : c),
  }));
  const setMatchAt = (i, patch) => setForm((f) => ({
    ...f, matches: f.matches.map((m, idx) => idx === i ? { ...m, ...patch } : m),
  }));
  const setInjuryAt = (i, patch) => setForm((f) => ({
    ...f, injuryConcerns: f.injuryConcerns.map((j, idx) => idx === i ? { ...j, ...patch } : j),
  }));

  // --- Style banner handlers ---
  const setStylePreference = (styleId) => {
    setField('style', styleId);
    setProfile((p) => ({ ...p, wrestlingStyle: styleId }));
    try { localStorage.setItem(STYLE_PROMPT_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    setStylePromptDismissed(true);
  };
  const dismissStylePrompt = () => {
    try { localStorage.setItem(STYLE_PROMPT_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    setStylePromptDismissed(true);
  };

  // --- Attachments ---
  const addAttachmentFiles = async (files) => {
    const payloads = await Promise.all(files.map((f) => fileToPayload(f)));
    setForm((f) => ({ ...f, attachments: [...f.attachments, ...payloads] }));
  };
  const addAttachmentUrl = async (url, kind) => {
    const payload = await urlToPayload(url, kind);
    if (payload) setForm((f) => ({ ...f, attachments: [...f.attachments, payload] }));
  };
  const removeAttachment = (idx) => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));

  // --- Save ---
  const handleSave = () => {
    setError('');
    if (!form.duration || form.duration <= 0) {
      setError('Please add a duration (minutes).');
      return;
    }
    const isToday = form.date === todayLocal;
    const sessionDate = isToday
      ? new Date().toISOString()
      : new Date(`${form.date}T12:00:00`).toISOString();

    const data = {
      date: sessionDate,
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: parseInt(form.duration, 10),
      style: form.style,
      classFormat: form.classFormat,
      focusModes: form.focusModes,
      focus: form.focusModes.join(', '),  // back-compat shorthand for list views
      intent: form.intent.trim(),
      reflection: form.reflection.trim(),
      techniques: form.techniques.filter((t) => t.name || t.subgroup),
      sparring: showSparring ? form.sparring : null,
      coachFeedback: form.coachFeedback.filter((c) => c.topic || c.note),
      weight: form.weight,
      matches: showMatchBlock ? form.matches.filter((m) => m.opponent) : [],
      injuryConcerns: form.injuryConcerns.filter((j) => j.area || j.note),
      notes: form.notes.trim(),
      attachments: form.attachments,
    };
    onSave(data);
    if (!isEditMode) clearDraft();
  };

  const showStylePrompt = !isEditMode && !profileStyle && !stylePromptDismissed;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => { clearDraft(); onClose(); }} data-testid="wrestling-log-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-enf-surface-border sticky top-0 bg-enf-surface-deep z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-semibold text-enf-text-muted">{isEditMode ? 'Edit session' : 'Log session'} · Wrestling</div>
            <div className="font-display text-2xl tracking-wide leading-none mt-1 text-enf-text-primary">Combatives Journal</div>
          </div>
          <button type="button" onClick={() => { clearDraft(); onClose(); }} className="p-2 hover:bg-enf-surface-elevated rounded-lg" data-testid="wrestling-close">
            <X className="w-4 h-4 text-enf-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Draft restored */}
          {restoredFromDraft && (
            <div className="px-3 py-2 bg-enf-role-info/10 border border-enf-role-info/40 rounded text-xs text-enf-text-secondary" data-testid="wrestling-restored-from-draft">
              Restored your in-progress entry from {formatTimeAgo(restoredFromDraft)}.
            </div>
          )}

          {/* Style banner — first time only, no onboarding card per founder */}
          {showStylePrompt && (
            <div className="px-4 py-3 bg-enf-surface-mid border border-enf-surface-border rounded-lg" data-testid="wrestling-style-prompt">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-2">Which style do you mostly train?</div>
              <div className="text-xs text-enf-text-muted mb-3">We&apos;ll default to this for new sessions. You can change it any time per session.</div>
              <div className="flex flex-wrap gap-2">
                {WRESTLING_STYLES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStylePreference(s.id)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-enf-surface-deep hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent transition-colors"
                    data-testid={`wrestling-style-pick-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
                <button type="button" onClick={dismissStylePrompt} className="px-3 py-1.5 rounded-md text-xs text-enf-text-muted hover:text-enf-text-primary" data-testid="wrestling-style-prompt-dismiss">
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* Date + Duration */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Date</label>
              <input type="date" value={form.date} onChange={(e) => setField('date', e.target.value)} max={todayLocal}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="wrestling-date" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Duration (min)</label>
              <input type="number" min={1} value={form.duration} onChange={(e) => setField('duration', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="wrestling-duration" />
            </div>
          </div>

          {/* Style + Class format */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Style</label>
              <select value={form.style} onChange={(e) => setField('style', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="wrestling-style">
                {WRESTLING_STYLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <div className="text-[10px] text-enf-text-muted mt-1">{styleMeta.description}</div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Class format</label>
              <select value={form.classFormat} onChange={(e) => setField('classFormat', e.target.value)}
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="wrestling-classformat">
                {WRESTLING_CLASS_FORMATS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Focus modes */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-2">Focus</label>
            <MultiSelectPills
              options={WRESTLING_FOCUS_MODES}
              value={form.focusModes}
              onChange={(v) => setField('focusModes', v)}
              testIdPrefix="wrestling-focus"
            />
          </div>

          {/* Intent */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Intent for today (optional)</label>
            <input type="text" value={form.intent} onChange={(e) => setField('intent', e.target.value)}
              placeholder="e.g., Work bottom escapes; clean up my single-leg finish"
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm" data-testid="wrestling-intent" />
          </div>

          {/* Techniques */}
          <CollapsibleSection title="Techniques worked" testId="wrestling-section-techniques" defaultOpen>
            <div className="space-y-2">
              {form.techniques.map((t, i) => (
                <WrestlingTechniqueRow
                  key={i}
                  index={i}
                  value={t}
                  styleId={form.style}
                  subgroupOptions={subgroupOptionsForPhase(t.phase)}
                  techniqueOptions={techniqueOptionsFor(t.phase, t.subgroup)}
                  onChange={(patch) => setTechAt(i, patch)}
                  onRemove={() => removeTechAt(i)}
                />
              ))}
              <button type="button" onClick={() => setField('techniques', [...form.techniques, newTechnique()])}
                className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                data-testid="wrestling-add-technique">
                <Plus className="w-3 h-3" /> Add technique
              </button>
            </div>
          </CollapsibleSection>

          {/* Sparring (conditional) */}
          {showSparring && (
            <CollapsibleSection title="Live wrestling" testId="wrestling-section-sparring" defaultOpen>
              <WrestlingSparringBlock
                value={form.sparring || newSparring()}
                onChange={(v) => setField('sparring', v)}
                styleMeta={styleMeta}
              />
            </CollapsibleSection>
          )}

          {/* Coach feedback */}
          <CollapsibleSection title="Coach feedback" testId="wrestling-section-coach">
            <div className="space-y-2">
              {form.coachFeedback.map((c, i) => (
                <div key={i} className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2" data-testid={`wrestling-coach-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <select value={c.topic} onChange={(e) => setCoachAt(i, { topic: e.target.value })}
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`wrestling-coach-topic-${i}`}>
                      <option value="">(topic)</option>
                      {WRESTLING_COACH_TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button type="button" onClick={() => setField('coachFeedback', form.coachFeedback.filter((_, idx) => idx !== i))}
                      className="ml-auto text-enf-text-muted hover:text-enf-accent-fg" data-testid={`wrestling-coach-remove-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <textarea value={c.note} onChange={(e) => setCoachAt(i, { note: e.target.value })}
                    placeholder="Coach's note — fill in the keywords now, expand later"
                    rows={2}
                    className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                    data-testid={`wrestling-coach-note-${i}`} />
                </div>
              ))}
              <button type="button" onClick={() => setField('coachFeedback', [...form.coachFeedback, newCoachNote()])}
                className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                data-testid="wrestling-add-coach">
                <Plus className="w-3 h-3" /> Add coach note
              </button>
            </div>
          </CollapsibleSection>

          {/* Matches (conditional) */}
          {showMatchBlock && (
            <CollapsibleSection title="Matches" testId="wrestling-section-matches" defaultOpen>
              <div className="space-y-2">
                {form.matches.map((m, i) => (
                  <div key={i} className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2" data-testid={`wrestling-match-row-${i}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={m.opponent} onChange={(e) => setMatchAt(i, { opponent: e.target.value })}
                        placeholder="Opponent" className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                        data-testid={`wrestling-match-opponent-${i}`} />
                      <select value={m.result} onChange={(e) => setMatchAt(i, { result: e.target.value })}
                        className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                        data-testid={`wrestling-match-result-${i}`}>
                        {WRESTLING_MATCH_RESULTS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    </div>
                    <input type="text" value={m.score} onChange={(e) => setMatchAt(i, { score: e.target.value })}
                      placeholder='Score / Finish e.g. "Fall 2:45", "Tech 16-0", "decision"'
                      className="w-full px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`wrestling-match-score-${i}`} />
                    <textarea value={m.notes} onChange={(e) => setMatchAt(i, { notes: e.target.value })}
                      placeholder="Key moments, what coach said after, what to work on"
                      rows={2} className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`wrestling-match-notes-${i}`} />
                    <button type="button" onClick={() => setField('matches', form.matches.filter((_, idx) => idx !== i))}
                      className="text-xs text-enf-text-muted hover:text-enf-accent-fg" data-testid={`wrestling-match-remove-${i}`}>
                      Remove match
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => setField('matches', [...form.matches, newMatch()])}
                  className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                  data-testid="wrestling-add-match">
                  <Plus className="w-3 h-3" /> Add match
                </button>
              </div>
            </CollapsibleSection>
          )}

          {/* Weight log */}
          <CollapsibleSection title="Weight & hydration" testId="wrestling-section-weight" icon={<Scale className="w-3 h-3 text-enf-text-muted" />}>
            <WrestlingWeightBlock
              value={form.weight || newWeight()}
              onChange={(v) => setField('weight', v)}
              flag={weightFlag}
            />
          </CollapsibleSection>

          {/* Injury concerns */}
          <CollapsibleSection title="Injury concerns" testId="wrestling-section-injury">
            <div className="space-y-2">
              {form.injuryConcerns.map((j, i) => (
                <div key={i} className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={j.area} onChange={(e) => setInjuryAt(i, { area: e.target.value })}
                      placeholder="Area (e.g., shoulder, knee, ear)"
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`wrestling-injury-area-${i}`} />
                    <input type="number" min={1} max={5} value={j.severity} onChange={(e) => setInjuryAt(i, { severity: parseInt(e.target.value, 10) || 1 })}
                      placeholder="Severity 1-5"
                      className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
                      data-testid={`wrestling-injury-severity-${i}`} />
                  </div>
                  <textarea value={j.note} onChange={(e) => setInjuryAt(i, { note: e.target.value })}
                    placeholder="What happened / what's tight" rows={2}
                    className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" />
                  <button type="button" onClick={() => setField('injuryConcerns', form.injuryConcerns.filter((_, idx) => idx !== i))}
                    className="text-xs text-enf-text-muted hover:text-enf-accent-fg">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => setField('injuryConcerns', [...form.injuryConcerns, newInjury()])}
                className="w-full px-3 py-2 bg-enf-surface-elevated hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs font-semibold flex items-center justify-center gap-2"
                data-testid="wrestling-add-injury">
                <Plus className="w-3 h-3" /> Log an injury concern
              </button>
            </div>
          </CollapsibleSection>

          {/* Reflection */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Reflection (what stuck with you)</label>
            <textarea value={form.reflection} onChange={(e) => setField('reflection', e.target.value)}
              placeholder="What you learned, where you got stuck, what to work on next"
              rows={3} className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm"
              data-testid="wrestling-reflection" />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-enf-text-muted mb-1">Additional notes</label>
            <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded text-sm"
              data-testid="wrestling-notes" />
          </div>

          {/* Attachments */}
          <CollapsibleSection title="Photos / video / links" testId="wrestling-section-attachments">
            <div className="space-y-3">
              <MediaCaptureMenu onFiles={addAttachmentFiles} recordingEtiquette />
              <PasteUrlInput onUrl={addAttachmentUrl} />
              {form.attachments.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {form.attachments.map((a, i) => (
                    <AttachmentTile key={i} attachment={a} onRemove={() => removeAttachment(i)} />
                  ))}
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Error */}
          {error && (
            <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded text-xs text-enf-accent-fg flex items-center gap-2" data-testid="wrestling-error">
              <AlertCircle className="w-3 h-3" /> {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-enf-surface-border bg-enf-surface-deep sticky bottom-0 flex items-center gap-2 z-10">
          <button type="button" onClick={() => { clearDraft(); onClose(); }}
            className="px-4 py-2 text-sm text-enf-text-secondary hover:text-enf-text-primary" data-testid="wrestling-cancel">
            Cancel
          </button>
          <button type="button" onClick={handleSave}
            className="flex-1 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover text-enf-text-primary rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="wrestling-save">
            {isEditMode ? 'Save changes' : 'Save session'}
          </button>
        </div>
      </div>
    </div>
  );
};


// --- Inline technique row (simpler than BJJ's TechniqueRow — no submissions/weapons) ---
const WrestlingTechniqueRow = ({ index, value, styleId, subgroupOptions, techniqueOptions, onChange, onRemove }) => {
  const set = (patch) => onChange(patch);
  return (
    <div className="bg-enf-surface-mid border border-enf-surface-border rounded p-3 space-y-2" data-testid={`wrestling-technique-${index}`}>
      <div className="grid grid-cols-3 gap-2">
        <select value={value.phase} onChange={(e) => set({ phase: e.target.value, subgroup: '', name: '' })}
          className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" data-testid={`wrestling-technique-phase-${index}`}>
          {WRESTLING_PHASES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <select value={value.subgroup} onChange={(e) => set({ subgroup: e.target.value, name: '' })}
          className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" data-testid={`wrestling-technique-subgroup-${index}`}>
          <option value="">(subgroup)</option>
          {subgroupOptions.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={value.mastery} onChange={(e) => set({ mastery: e.target.value })}
          className="px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs" data-testid={`wrestling-technique-mastery-${index}`}>
          <option value="">(mastery)</option>
          {WRESTLING_MASTERY_LEVELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input type="text" value={value.name} onChange={(e) => set({ name: e.target.value })}
          list={`wrestling-tech-options-${index}`}
          placeholder="Technique name (pick a suggestion or type your own)"
          className="flex-1 px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
          data-testid={`wrestling-technique-name-${index}`} />
        <datalist id={`wrestling-tech-options-${index}`}>
          {techniqueOptions.map((t) => <option key={t} value={t} />)}
        </datalist>
        <input type="number" min={0} value={value.reps} onChange={(e) => set({ reps: e.target.value })}
          placeholder="Reps" className="w-20 px-2 py-1 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
          data-testid={`wrestling-technique-reps-${index}`} />
        <button type="button" onClick={onRemove} className="text-enf-text-muted hover:text-enf-accent-fg" data-testid={`wrestling-technique-remove-${index}`}>
          <X className="w-3 h-3" />
        </button>
      </div>
      <textarea value={value.note} onChange={(e) => set({ note: e.target.value })}
        placeholder="Detail / what worked / what didn't" rows={1}
        className="w-full px-2 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded text-xs"
        data-testid={`wrestling-technique-note-${index}`} />
    </div>
  );
};


// --- Sparring block (style-aware) ---
const WrestlingSparringBlock = ({ value, onChange, styleMeta }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <NumCell k="liveGoCount" label="Live go&apos;s" value={value} set={set} testId="wrestling-spar-livegoes" />
        <NumCell k="partnersWorkedWith" label="Partners" value={value} set={set} testId="wrestling-spar-partners" />
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Intensity</span>
          <select value={value.intensityRating} onChange={(e) => set({ intensityRating: parseInt(e.target.value, 10) })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs text-enf-text-primary" data-testid="wrestling-spar-intensity">
            {WRESTLING_FELT_SCALE.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <NumCell k="takedownsScored" label="TDs scored" value={value} set={set} testId="wrestling-spar-tds-scored" />
        <NumCell k="takedownsConceded" label="TDs conceded" value={value} set={set} testId="wrestling-spar-tds-conceded" />
        <NumCell k="escapesEarned" label="Escapes earned" value={value} set={set} testId="wrestling-spar-escapes-earned" />
        <NumCell k="escapesConceded" label="Escapes given up" value={value} set={set} testId="wrestling-spar-escapes-conceded" />
        <NumCell k="reversalsScored" label="Reversals" value={value} set={set} testId="wrestling-spar-reversals-scored" />
        <NumCell k="reversalsConceded" label="Got reversed" value={value} set={set} testId="wrestling-spar-reversals-conceded" />
        <NumCell k="turnsScored" label="Turns (you)" value={value} set={set} testId="wrestling-spar-turns-scored" />
        <NumCell k="turnsConceded" label="Turns (against)" value={value} set={set} testId="wrestling-spar-turns-conceded" />
        <NumCell k="pinsScored" label="Pins" value={value} set={set} testId="wrestling-spar-pins-scored" />
        <NumCell k="pinsConceded" label="Got pinned" value={value} set={set} testId="wrestling-spar-pins-conceded" />
        {styleMeta.ridingTime && <NumCell k="ridingTimeSeconds" label="Riding time (sec)" value={value} set={set} testId="wrestling-spar-rt" />}
        {styleMeta.pushOut && <NumCell k="pushoutsScored" label="Push-outs" value={value} set={set} testId="wrestling-spar-pushouts-scored" />}
        {styleMeta.pushOut && <NumCell k="pushoutsConceded" label="Pushed out" value={value} set={set} testId="wrestling-spar-pushouts-conceded" />}
      </div>
    </div>
  );
};

// --- Weight block (with anti-shame guardrails) ---
const WrestlingWeightBlock = ({ value, onChange, flag }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="space-y-3">
      <div className="px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded text-xs text-enf-text-muted flex items-start gap-2" data-testid="wrestling-weight-disclaimer">
        <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-enf-role-info" />
        <span>
          We&apos;re a notebook, not a doctor. Logging weight here helps you spot patterns, not plan a cut. For cut planning, work with your coach or a sports dietitian — start with these references: {WRESTLING_WEIGHT_REFERENCES.map((r, i) => (
            <React.Fragment key={r.url}>
              {i > 0 && ' · '}
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-enf-text-primary">{r.label}</a>
            </React.Fragment>
          ))}.
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Today&apos;s weight (lb)</span>
          <input type="number" step="0.1" value={value.scaleWeightLb} onChange={(e) => set({ scaleWeightLb: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="wrestling-weight-scale" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Target class (lb)</span>
          <input type="number" value={value.targetClassLb} onChange={(e) => set({ targetClassLb: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="wrestling-weight-target" />
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Days to weigh-in</span>
          <input type="number" min={0} value={value.daysToWeighIn} onChange={(e) => set({ daysToWeighIn: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="wrestling-weight-days" />
        </label>
        <label className="block col-span-2 sm:col-span-2">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Cut method</span>
          <select value={value.cutMethod} onChange={(e) => set({ cutMethod: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="wrestling-weight-cutmethod">
            <option value="">(select)</option>
            {WRESTLING_CUT_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.15em] text-enf-text-muted mb-1">Hydration</span>
          <select value={value.hydrationStatus} onChange={(e) => set({ hydrationStatus: e.target.value })}
            className="w-full px-2 py-1 bg-enf-surface-mid border border-enf-surface-border rounded text-xs" data-testid="wrestling-weight-hydration">
            <option value="">(self-check)</option>
            {WRESTLING_HYDRATION.map((h) => <option key={h.id} value={h.id}>{h.label} — {h.help}</option>)}
          </select>
        </label>
      </div>
      {flag && (
        <div
          className={`px-3 py-2 rounded text-xs flex items-start gap-2 border ${
            flag.tone === 'urgent'
              ? 'bg-enf-role-danger/15 border-enf-role-danger/40 text-enf-accent-fg'
              : 'bg-enf-accent/10 border-enf-accent-fg/40 text-enf-text-primary'
          }`}
          data-testid="wrestling-weight-flag"
        >
          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{flag.message}</span>
        </div>
      )}
      <textarea value={value.notes} onChange={(e) => set({ notes: e.target.value })}
        placeholder="How you are feeling, what you ate, energy level — anything to remember next week"
        rows={2} className="w-full px-2 py-1.5 bg-enf-surface-mid border border-enf-surface-border rounded text-xs"
        data-testid="wrestling-weight-notes" />
    </div>
  );
};

export default WrestlingLogDialog;
